type NodeEnv = 'development' | 'production' | 'test';

const rawEnv = process.env;

const REQUIRED_ENV_KEYS = ['REACT_APP_BACKEND_URL', 'REACT_APP_BACKEND', 'REACT_APP_I18N_FALLBACK_LNG'] as const;

type RequiredEnvKey = typeof REQUIRED_ENV_KEYS[number];
type OptionalEnvKey =
  | 'PUBLIC_URL'
  | 'REACT_APP_BACKEND_URL_V2'
  | 'REACT_APP_I18N_DEBUG'
  | 'REACT_APP_I18N_ESCAPE_VALUE'
  | 'REACT_APP_VERSION';

const isMissingValue = (value?: string): value is undefined => value == null || value.trim() === '';

const missingRequiredEnvKeys = REQUIRED_ENV_KEYS.filter(key => isMissingValue(rawEnv[key]));

if (missingRequiredEnvKeys.length > 0) {
  throw new Error(`[env] Missing required environment variables: ${missingRequiredEnvKeys.join(', ')}`);
}

const nodeEnv = rawEnv.NODE_ENV;

if (nodeEnv !== 'development' && nodeEnv !== 'production' && nodeEnv !== 'test') {
  throw new Error(`[env] Invalid NODE_ENV value: ${String(nodeEnv)}`);
}

const getRequiredEnv = (key: RequiredEnvKey): string => {
  const value = rawEnv[key];

  if (isMissingValue(value)) {
    throw new Error(`[env] Missing required environment variable: ${key}`);
  }

  return value;
};

const getOptionalEnv = (key: OptionalEnvKey): string | undefined => {
  const value = rawEnv[key];

  return isMissingValue(value) ? undefined : value;
};

const getBooleanEnv = (key: OptionalEnvKey, fallback: boolean): boolean => {
  const value = getOptionalEnv(key);

  if (value == null) {
    return fallback;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  throw new Error(`[env] Expected ${key} to be "true" or "false", received "${value}"`);
};

export const env = Object.freeze({
  nodeEnv: nodeEnv as NodeEnv,
  isDevelopment: nodeEnv === 'development',
  isProduction: nodeEnv === 'production',
  publicUrl: getOptionalEnv('PUBLIC_URL') ?? '',
  backendUrl: getRequiredEnv('REACT_APP_BACKEND_URL'),
  backendUrlV2: getOptionalEnv('REACT_APP_BACKEND_URL_V2'),
  backendOrigin: getRequiredEnv('REACT_APP_BACKEND'),
  version: getOptionalEnv('REACT_APP_VERSION'),
  i18n: {
    debug: getBooleanEnv('REACT_APP_I18N_DEBUG', false),
    escapeValue: getBooleanEnv('REACT_APP_I18N_ESCAPE_VALUE', false),
    fallbackLng: getRequiredEnv('REACT_APP_I18N_FALLBACK_LNG'),
  },
});
